import React from 'react';
import { FormGroup, Grid, makeStyles } from '@material-ui/core';
import i18n from 'i18n';
import { ClientFields, CreateClientProps } from 'modules/clients/interfaces';
import { cyrb53Hash } from 'utils/helpers';
import styles from './styles';
import ClientFormControl from './ClientFormControl';

const useStyles = makeStyles(styles);

export type SetFieldValue = (
  field: string,
  value: any,
  shouldValidate?: boolean | undefined
) => void;

export interface ClientSwitchOptionsProps {
  values: ClientFields | CreateClientProps;
  setFieldValue: SetFieldValue;
}

interface SwitchOption {
  id: string;
  checked: boolean;
  label: string;
  children?: JSX.Element;
  handleChange: (e, value) => void;
}

const ClientSwitchOptions: React.FC<ClientSwitchOptionsProps> = ({
  values,
  setFieldValue,
}) => {
  const classes = useStyles();

  const optionsSwitch: Array<SwitchOption> = React.useMemo(
    () => [
      {
        id: 'toggle-copy-admin',
        checked: !!values?.copy_admin,
        label: i18n.t('CLIENTS_PAGE.ALWAYS_COPY_ADMIN_KEY'),
        handleChange: (e, value) => setFieldValue('copy_admin', !!value),
      },
      {
        id: 'toggle-block-multi-session',
        checked: !!values?.licenses?.block_same_user_multiple_session,
        label: i18n.t('CLIENTS_PAGE.BLOCK_MULTIPLE_SESSIONS_KEY'),
        handleChange: (e, value) =>
          setFieldValue('licenses.block_same_user_multiple_session', !!value),
      },
      {
        id: 'toggle-use-ios-devices',
        checked: !!values?.user_connection?.use_ios_devices,
        label: i18n.t('CLIENTS_PAGE.USE_IOS_DEVICES_KEY'),
        handleChange: (e, value) =>
          setFieldValue('user_connection.use_ios_devices', !!value),
      },
      {
        id: 'toggle-remote-profile',
        checked: !!values?.active_directory?.remote_profile,
        label: i18n.t('REMOTE_PROFILE_KEY'),
        handleChange: (e, value) =>
          setFieldValue('active_directory.remote_profile', !!value),
      },
    ],
    [setFieldValue, values]
  );

  return (
    <FormGroup
      className={classes.formGroup}
      data-testid={'autosky-clientswitchoptions'}
    >
      {optionsSwitch?.map(option => (
        <Grid
          item
          xs={12}
          key={`client-switch-${cyrb53Hash(String(option?.label))}`}
          className={classes.switchOption}
        >
          <ClientFormControl
            id={option?.id}
            checked={option?.checked}
            handleChange={option?.handleChange}
            label={option?.label}
            classes={{ colorSecondary: classes.switchChecked }}
            className={classes.formLabel}
            typographyClassName={classes.switchText}
          />
          {option?.children}
        </Grid>
      ))}
    </FormGroup>
  );
};

export default ClientSwitchOptions;
